import { makeAutoObservable, runInAction } from "mobx";
import { rolesPermissionsService } from "../services";

const resolvePayload = (data) => {
  if (!data) return null;
  if (data && typeof data === "object" && "data" in data) {
    return data.data;
  }
  return data;
};

class RolesPermissionsStore {
  permissionsModules = [];
  loadingModules = false;
  dialogOpen = false;
  deleteDialogOpen = false;
  selectedRole = null;
  creatingRole = false;
  updatingRole = false;
  deletingRoleId = null;
  roles = [];
  loadingRoles = false;

  constructor() {
    makeAutoObservable(this);
  }

  setDialogOpen = (open) => {
    this.dialogOpen = open;
  };

  openCreateDialog = () => {
    this.selectedRole = null;
    this.dialogOpen = true;
  };

  openEditDialog = (role) => {
    this.selectedRole = role?.raw || role || null;
    this.dialogOpen = true;
  };

  closeDialog = () => {
    this.dialogOpen = false;
    this.selectedRole = null;
  };

  openDeleteDialog = (role) => {
    this.selectedRole = role?.raw || role || null;
    this.deleteDialogOpen = true;
  };

  closeDeleteDialog = () => {
    this.deleteDialogOpen = false;
    this.selectedRole = null;
  };

  get dialogMode() {
    return this.selectedRole ? "edit" : "create";
  }

  fetchPermissionModules = async (params = {}) => {
    this.loadingModules = true;
    try {
      const data = await rolesPermissionsService.listModules(params, {
        showErrorToast: false,
      });
      const payload = resolvePayload(data) || {};
      const modules = payload.modules || payload?.data?.modules || [];
      runInAction(() => {
        this.permissionsModules = Array.isArray(modules) ? modules : [];
      });
      return data;
    } catch (_error) {
      runInAction(() => {
        this.permissionsModules = [];
      });
      return false;
    } finally {
      runInAction(() => {
        this.loadingModules = false;
      });
    }
  };

  fetchRoleList = async () => {
    this.loadingRoles = true;
    try {
      const data = await rolesPermissionsService.listRoles({
        showErrorToast: false,
      });
      const payload = resolvePayload(data) || {};
      const roles = payload.roles || payload?.data?.roles || payload || [];
      runInAction(() => {
        this.roles = Array.isArray(roles) ? roles : [];
      });
      return data;
    } catch (_error) {
      runInAction(() => {
        this.roles = [];
      });
      return false;
    } finally {
      runInAction(() => {
        this.loadingRoles = false;
      });
    }
  };

  createRole = async (payload) => {
    this.creatingRole = true;
    try {
      const data = await rolesPermissionsService.createRole(payload, {
        showErrorToast: false,
      });
      await this.fetchRoleList();
      return data;
    } finally {
      runInAction(() => {
        this.creatingRole = false;
      });
    }
  };

  updateRole = async (roleId, payload) => {
    if (!roleId) {
      throw new Error("Role id is required to update a role.");
    }
    this.updatingRole = true;
    try {
      const data = await rolesPermissionsService.updateRole(roleId, payload, {
        showErrorToast: false,
      });
      await this.fetchRoleList();
      return data;
    } finally {
      runInAction(() => {
        this.updatingRole = false;
      });
    }
  };

  deleteRole = async (roleId) => {
    if (!roleId) {
      throw new Error("Role id is required to delete a role.");
    }
    this.deletingRoleId = roleId;
    try {
      const data = await rolesPermissionsService.deleteRole(roleId, {
        showErrorToast: false,
      });
      await this.fetchRoleList();
      return data;
    } finally {
      runInAction(() => {
        this.deletingRoleId = null;
      });
    }
  };
}

export const rolesPermissionsStore = new RolesPermissionsStore();
